import React, { useState } from 'react';
import { FilterX, List, Table, Plus, Pencil, Save, X, Trash2, Loader2 } from 'lucide-react';
import ActionMenu from './ActionMenu';
import SearchClearIcon from './SearchClearIcon';
import ColumnSettingsModal from './ColumnSettingsModal';
import exportReportExcel from '../../modules/reports/export/reportExcelExporter';
import { useDataTransfer } from '../../context/DataTransferContext';
import { SEARCH_HINT, hasColumnFilters } from '../../utils/listSearch';

const ModuleHeader = ({
  viewMode,
  setViewMode,
  searchTerm,
  setSearchTerm,
  searchPlaceholder,
  filters,
  clearAllFilters,
  filteredCount,
  totalCount,
  columns,
  filteredData,
  filename,
  // CRUD props for list view
  onAdd,
  onDelete,
  onEdit,
  onSave,
  onCancel,
  canEdit = true,
  canSave = false,
  canCancel = false,
  isEditing = false,
  isNewRecord = false,
  // Datasheet props
  editModeEnabled = false,
  onEditModeToggle,
  isDirty = false,
  onDatasheetSave,
  onDatasheetCancel,
  datasheetSaving = false,
  // Column settings props
  columnOrder,
  hiddenColumns,
  frozenColumns,
  onColumnSettingsChange,
  // Permission
  userCanEdit = true,
  canDownload = true,
  // Delete mode props
  deleteModeEnabled = false,
  selectedDeleteCount = 0,
  onEnterDeleteMode,
  onCancelDeleteMode,
  onConfirmDelete,
  onSearchSubmit,
  searchLoading = false,
  forceViewType = null,
  headerTabs = null,
  onChangeHistory = null,
  onPurge = null,
  showPurge = false,
  // Page-specific 6-dot entries (ActionMenu `items`), e.g. Advanced Search
  actionItems = null,
}) => {
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const { registerActivity, updateActivity, completeActivity, failActivity } = useDataTransfer();

  const handleDownload = async () => {
    const exportName = 'Custom ' + (filename || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    const activityId = `download-${filename}-${Date.now()}`;
    const exportCols = columns.map(c => ({ field: c.key, label: c.label }));

    registerActivity(activityId, {
      name: exportName, type: 'download', format: 'excel', phase: 'Preparing…', pct: 0,
      rowsTransferred: filteredData.length,
    });

    try {
      await exportReportExcel({
        config: { reportName: exportName },
        columns: exportCols,
        data: filteredData,
        isCancelled: () => false,
        onProgress: (msg) => updateActivity(activityId, { phase: msg || 'Building Excel…' })
      });
      completeActivity(activityId);
    } catch {
      failActivity(activityId);
    }
  };
  const hasActiveFilters = hasColumnFilters(filters);

  return (
    <div className="stui-module-header">
      <div className="stui-module-header-left">
        {headerTabs ? (
          <div className="stui-module-header-tabs">{headerTabs}</div>
        ) : (
          <div className="stui-module-header-tabs">
            {!forceViewType && <button
              onClick={() => setViewMode('list')}
              className={`stui-tab-btn-icon ${viewMode === 'list' ? 'active' : ''}`}
              title="List View"
            >
              <List size={18} />
            </button>}
            {!forceViewType && <button
              onClick={() => setViewMode('datasheet')}
              className={`stui-tab-btn-icon ${viewMode === 'datasheet' ? 'active' : ''}`}
              title="Datasheet View"
            >
              <Table size={18} />
            </button>}
          </div>
        )}
      </div>

      <div className="stui-module-header-actions">
        {/* CRUD Buttons - List View Only, hidden for Guest users */}
        {viewMode === 'list' && userCanEdit && (
          <div className="stui-module-header-crud">
            {isEditing ? (
              onDelete && (
                <button
                  onClick={onDelete}
                  className={`stui-crud-btn stui-delete-btn ${isNewRecord ? 'disabled' : ''}`}
                  title="Delete Record"
                  disabled={isNewRecord}
                >
                  <Trash2 size={18} />
                </button>
              )
            ) : (
              onAdd && (
                <button
                  onClick={onAdd}
                  className="stui-crud-btn"
                  title="Add New Record"
                >
                  <Plus size={18} />
                </button>
              )
            )}
            {/* Edit/Cancel toggle - Pencil when not editing, X when editing */}
            <button
              onClick={isEditing ? onCancel : onEdit}
              className={`stui-crud-btn ${!canEdit && !isEditing ? 'disabled' : ''}`}
              title={isEditing ? 'Cancel' : 'Edit Record'}
              disabled={!canEdit && !isEditing}
            >
              {isEditing ? <X size={18} /> : <Pencil size={18} />}
            </button>
            <button
              onClick={onSave}
              className={`stui-crud-btn ${!canSave ? 'disabled' : ''}`}
              title="Save Changes"
              disabled={!canSave}
            >
              <Save size={18} />
            </button>
          </div>
        )}

        {/* Datasheet Controls - Datasheet View Only */}
        {viewMode === 'datasheet' && (
          <div className="stui-module-header-datasheet-controls">
            {/* Delete Mode Controls - replaces Edit/Save when in delete mode */}
            {deleteModeEnabled && userCanEdit && (
              <div className="stui-module-header-crud">
                <button
                  onClick={onCancelDeleteMode}
                  className="stui-crud-btn"
                  title="Cancel Delete Mode"
                >
                  <X size={18} />
                </button>
                <button
                  onClick={onConfirmDelete}
                  className={`stui-crud-btn stui-delete-btn ${selectedDeleteCount === 0 ? 'disabled' : ''}`}
                  title={selectedDeleteCount > 0 ? `Delete ${selectedDeleteCount} selected` : 'Select rows to delete'}
                  disabled={selectedDeleteCount === 0}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            )}
            {/* Edit/Save - hidden for Guest users, hidden in delete mode */}
            {!deleteModeEnabled && userCanEdit && onEditModeToggle && (
              <div className="stui-module-header-crud">
                {/* Edit/Cancel toggle - Pencil when not editing, X when editing */}
                <button
                  onClick={onEditModeToggle}
                  className="stui-crud-btn"
                  title={editModeEnabled ? 'Cancel' : 'Enable Editing'}
                  disabled={datasheetSaving}
                >
                  {editModeEnabled ? <X size={18} /> : <Pencil size={18} />}
                </button>
                <button
                  onClick={onDatasheetSave}
                  className={`stui-crud-btn ${!isDirty || datasheetSaving ? 'disabled' : ''}`}
                  title="Save Changes"
                  disabled={!isDirty || datasheetSaving}
                >
                  {datasheetSaving ? <Loader2 size={18} className="stui-spinner" /> : <Save size={18} />}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Clear filter — immediately left of search, no reserved space */}
        {hasActiveFilters && (
          <button onClick={clearAllFilters} title="Clear all filters" className="stui-clear-filter-btn">
            <FilterX size={16} />
            <span>{filteredCount}/{totalCount}</span>
          </button>
        )}

        <div className="stui-search-wrapper">
          {searchLoading
            ? <Loader2 size={16} className="stui-search-icon spinning" />
            : <SearchClearIcon
                searchTerm={searchTerm}
                onClear={() => { setSearchTerm(''); onSearchSubmit?.(''); }}
              />
          }
          <input
            type="text"
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
              if (!e.target.value) onSearchSubmit?.('');
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onSearchSubmit?.(e.target.value);
              }
            }}
            placeholder={searchPlaceholder}
            title={SEARCH_HINT}
            className="stui-search-input"
          />
        </div>
        <ActionMenu
          items={actionItems}
          // List view without a selection used to leave nothing to show; page items (Advanced
          // Search) must stay reachable exactly when a search has emptied the list.
          disabled={viewMode === 'list' && !onChangeHistory && !actionItems?.length}
          onDownload={canDownload ? handleDownload : undefined}
          onModifyView={() => setShowColumnSettings(true)}
          showModifyView={viewMode === 'datasheet'}
          onBulkDelete={onEnterDeleteMode}
          showBulkDelete={viewMode === 'datasheet' && userCanEdit && !deleteModeEnabled && !editModeEnabled && !!onEnterDeleteMode}
          showChangeHistory={!!onChangeHistory}
          onChangeHistory={onChangeHistory}
          showPurge={showPurge}
          onPurge={onPurge}
        />
      </div>
      <ColumnSettingsModal
        isOpen={showColumnSettings}
        onClose={() => setShowColumnSettings(false)}
        columns={columns}
        columnOrder={columnOrder}
        hiddenColumns={hiddenColumns}
        frozenColumns={frozenColumns}
        onApply={onColumnSettingsChange}
      />
    </div>
  );
};

export default ModuleHeader;
